/**
 * Índice `quote_id → nomes dos itens`, para a busca da lista de orçamentos
 * enxergar PRODUTO e SERVIÇO — e para ela saber quando NÃO está enxergando.
 *
 * ─── POR QUE NÃO BASTA O CACHE ──────────────────────────────────────────────
 *
 * O `OfflineDataCacheWarmup` já traz os itens de TODOS os orçamentos da empresa
 * e os grava em `quote._offline_items`. Na maior parte das aberturas isso é
 * suficiente e sai de graça. Mas ele roda no boot e pode não ter terminado (ou
 * ter falhado), e aí um documento fica sem itens no cache — indistinguível, na
 * tela, de um documento que não tem o produto procurado. O usuário buscaria
 * "Cabo", veria 2 de 3 resultados e concluiria que o terceiro não existe.
 *
 * É corte silencioso, que é o que este repo proíbe (#50/#61). Então:
 *
 * 1. o cache é a fonte IMEDIATA (a busca funciona no primeiro caractere);
 * 2. a busca dispara UMA leitura paginada dos itens da empresa, que completa o
 *    índice — paginada porque `quote_items` passa de 1.000 linhas com folga e o
 *    PostgREST corta sem avisar (#50);
 * 3. enquanto isso não resolve, `complete` é `false` e **a tela diz** que a
 *    busca em itens ainda está parcial.
 *
 * A leitura é PREGUIÇOSA de propósito: quem nunca usa a busca nunca paga por
 * ela. E é feita uma vez por empresa, não por tecla.
 */
import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { fetchAllPages } from '@/lib/reportPaging';

export interface QuoteItemNameIndex {
  /** `quote_id` → nomes dos itens daquele documento. */
  byQuote: Map<string, string[]>;
  /**
   * `true` = o índice veio do servidor e cobre a empresa inteira.
   * `false` = só o que estava no cache; a busca em itens pode estar incompleta.
   */
  complete: boolean;
  /** A leitura está em curso. */
  loading: boolean;
}

/** Nomes dos itens que o CACHE já conhece (`_offline_items` do warmup). */
export function itemNamesFromCachedQuotes(quotes: readonly unknown[]): Map<string, string[]> {
  const byQuote = new Map<string, string[]>();
  for (const raw of quotes) {
    const quote = raw as { id?: unknown; _offline_items?: unknown };
    if (!quote?.id) continue;
    const items = Array.isArray(quote._offline_items) ? quote._offline_items : null;
    if (!items) continue;
    byQuote.set(
      String(quote.id),
      items
        .map((item) => (item as { name?: unknown })?.name)
        .filter((name): name is string => typeof name === 'string' && name.length > 0),
    );
  }
  return byQuote;
}

/**
 * `enabled` liga a leitura — a tela passa `true` quando há termo de busca.
 * Uma vez completo, o índice não é relido enquanto a empresa não mudar.
 */
export function useQuoteItemNames(
  companyId: string | null | undefined,
  quotes: readonly unknown[],
  enabled: boolean,
  isOnline: boolean,
): QuoteItemNameIndex {
  const [server, setServer] = useState<{ companyId: string; byQuote: Map<string, string[]> } | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  /** Empresa cuja leitura já foi disparada — impede refetch a cada tecla. */
  const requestedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !companyId || !isOnline) return;
    if (requestedFor.current === companyId) return;
    requestedFor.current = companyId;

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const { rows } = await fetchAllPages<{ quote_id: string; name: string | null }>(
          (from, to) =>
            supabase
              .from('quote_items')
              .select('quote_id, name')
              // A RLS já recorta por empresa, mas a ORDEM explícita é o que
              // torna a paginação determinística: sem ela o Postgres pode
              // devolver a mesma linha em duas páginas e omitir outra.
              .order('quote_id', { ascending: true })
              .order('id', { ascending: true })
              // `as any` pelo mesmo motivo dos outros chamadores de
              // `fetchAllPages` (`useReportDimensions`): o builder do
              // PostgREST é *thenable*, mas o tipo dele não é uma `Promise`.
              .range(from, to) as any,
        );
        if (cancelled) return;

        const byQuote = new Map<string, string[]>();
        for (const row of rows) {
          if (!row?.quote_id || typeof row.name !== 'string' || !row.name) continue;
          const list = byQuote.get(row.quote_id);
          if (list) list.push(row.name);
          else byQuote.set(row.quote_id, [row.name]);
        }
        setServer({ companyId, byQuote });
      } catch (e) {
        // Falhou: o índice fica INCOMPLETO e a tela diz isso. Uma nova tentativa
        // é possível ao trocar de empresa ou reabrir a tela — insistir a cada
        // tecla transformaria uma busca numa enxurrada de requisições.
        console.error('[useQuoteItemNames] falha ao indexar itens:', e);
        requestedFor.current = null;
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, companyId, isOnline]);

  const fromCache = itemNamesFromCachedQuotes(quotes);
  const complete = !!server && server.companyId === companyId;

  if (!complete) return { byQuote: fromCache, complete: false, loading };

  // O servidor manda, mas o cache COMPLEMENTA: um orçamento criado offline não
  // existe no Postgres e seus itens só vivem em `_offline_items`. Preferir o
  // servidor cegamente apagaria da busca justamente o que o usuário acabou de
  // criar sem rede.
  const byQuote = new Map(server.byQuote);
  for (const [quoteId, names] of fromCache) {
    if (!byQuote.has(quoteId)) byQuote.set(quoteId, names);
  }
  return { byQuote, complete: true, loading };
}
